import { generalizeUrl } from "./urlGeneralize";

const UUID = "[a-f\\d]{8}-[a-f\\d]{4}-[a-f\\d]{4}-[a-f\\d]{4}-[a-f\\d]{12}";
const identifierStart = "(^|[^a-z\\d])";
const identifierEnd = "(?![a-z\\d])";
const uploadId = new RegExp(`${identifierStart}\\d{4}_\\d{2}_\\d{2}_${UUID}${identifierEnd}`, "gi");
const attachmentId = new RegExp(
	`${identifierStart}\\d{10,}-${UUID}(?=\\.pdf${identifierEnd})`,
	"gi"
);
const uuid = new RegExp(`${identifierStart}${UUID}${identifierEnd}`, "gi");

const generalizeIdentifiers = (message: string): string =>
	message.replace(uploadId, "$1{id}").replace(attachmentId, "$1{id}").replace(uuid, "$1{id}");

const generalizeResourceUrl = (url: string): string =>
	generalizeUrl(generalizeIdentifiers(url), ["version"]);

type MessageRule = {
	pattern: RegExp;
	message: (match: RegExpMatchArray) => string;
};

const messageRules: Record<string, MessageRule> = {
	eventPayload: {
		pattern: /^(Could not post event with eventName: [^\r\n]+?) with data:[\s\S]*$/,
		message: match => match[1],
	},
	malformedTimestamp: {
		pattern: /^Malformed timestamp:[\s\S]*$/,
		message: () => "Malformed timestamp",
	},
	uploadCompletion: {
		pattern: /^Error completing upload\. completeDoc:[\s\S]*$/,
		message: () => "Error completing upload",
	},
	dynamicImport: {
		pattern:
			/^((?:Failed to load component [^\r\n]+?\. Reason: )?(?:(?:TypeError|Error): )?Failed to fetch dynamically imported module):\s*\S[\s\S]*$/,
		message: match => match[1],
	},
	apiLoop: {
		pattern: /^(API_CALL_LOOP_)((?:https?:\/\/[^/\s]+)?\/\S*)$/,
		message: match => match[1] + generalizeResourceUrl(match[2]),
	},
	httpResource: {
		pattern:
			/^((?:(?:TypeError|Error): )?(?:Request failed with status code \d+|Network Error) for )((?:https?:\/\/[^/\s]+)?\/\S*)$/,
		message: match => match[1] + generalizeResourceUrl(match[2]),
	},
	vimeoResource: {
		pattern: /^([“"]https:\/\/vimeo\.com\/)\d+\/[a-zA-Z\d]+([”"] was not found\.)$/,
		message: match => `${match[1]}{id}/{id}${match[2]}`,
	},
	jsonLocation: {
		pattern:
			/^((?:Expected |Unexpected )[^\r\n]*JSON) at position \d+(?: \(line \d+ column \d+\))?$/,
		message: match => match[1],
	},
	firefoxJsonLocation: {
		pattern: /^(JSON\.parse: [^\r\n]+) at line \d+ column \d+ of the JSON data$/,
		message: match => match[1],
	},
	jsonExcerpt: {
		pattern: /^(Unexpected token '[^']+'),\s*"[\s\S]*"\.\.\. is not valid JSON$/,
		message: match => `${match[1]}: invalid JSON`,
	},
	videoDuration: {
		pattern:
			/^(Seconds must be a positive number less than the duration of the video) \(\d+(?:\.\d+)?\)\.$/,
		message: match => `${match[1]}.`,
	},
	cueNumber: {
		pattern: /^(End must be greater than start) \(cue #\d+\)$/,
		message: match => match[1],
	},
	queueCount: {
		pattern: /^(RTC_COMMANDS_QUEUE_STUCK)_\d+$/,
		message: match => match[1],
	},
	hlsPlaylist: {
		pattern: /^(VIDEOJS: ERROR: \(CODE:\d+ [^)]+\) HLS playlist request error) at URL: [\s\S]+$/,
		message: match => match[1],
	},
	videoJsObject: {
		pattern: /^(VIDEOJS: ERROR: [^\r\n]+?) \[object [a-zA-Z_$][\w$]*\]$/,
		message: match => match[1],
	},
	messageObject: {
		pattern:
			/^(Unable to process (?:the YouTube URL|WeVideo selection|selected video)\.|Unable to load Kaltura video\.|Error initializing playlist:|Uppy upload error:|Error handling bulb completion message:|Canvas Studio request failed\.) \[object [a-zA-Z_$][\w$]*\]$/,
		message: match => match[1].replace(/:$/, ""),
	},
	pluginUrl: {
		pattern: /^(Failed to load plugin: [^\r\n]+?) from url [\s\S]+$/,
		message: match => match[1],
	},
	cssUrl: {
		pattern: /^(Unable to preload CSS) for [\s\S]+$/,
		message: match => match[1],
	},
	popupUrl: {
		pattern: /^(\[GSI_LOGGER\]: Failed to open popup window) on url: [\s\S]+$/,
		message: match => match[1],
	},
	socketId: {
		pattern: /^(Trying to push on socket )socket_\d+( of type: [^\r\n]+, but we are offline!)$/,
		message: match => `${match[1]}{id}${match[2]}`,
	},
	objectLookupId: {
		pattern:
			/^(Non-Error promise rejection captured with value: Object Not Found Matching Id:)\d+(, MethodName:[^,\r\n]+, ParamCount:\d+)$/,
		message: match => `${match[1]}{id}${match[2]}`,
	},
	regexInput: {
		pattern:
			/^Invalid regular expression: \/[^\r\n]*\/[a-z]*: (Unterminated group|Unterminated character class)$/,
		message: match => `Invalid regular expression: ${match[1]}`,
	},
};

export const generalizeMessage = (message: string): string => {
	for (const rule of Object.values(messageRules)) {
		const match = message.match(rule.pattern);
		if (match) return generalizeIdentifiers(rule.message(match));
	}
	return generalizeIdentifiers(message);
};
